import { languages } from "@aichat/constants/languages";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { Dispatch, SetStateAction } from "react";
import { WidgetConfig } from "@aichat/types/widget";
import { getHSLAString, useFlags } from "@aichat/aichat-lib";
import { FlagsmithTraits } from "@aichat/types/enums/index";

interface ISelectLanguageProps {
  language: string | null;
  setLanguage: Dispatch<SetStateAction<string | null>>;
  supportedLanguages?: { code: string }[];
  config: WidgetConfig;
}

const LANGUAGES = Object.entries(languages).map(([code, label]) => ({
  code,
  label,
}));

const SelectLanguageSchema = z.object({
  language: z.string({
    required_error: "Please select a language",
  }),
});

export default function SelectLanguage({
  language,
  setLanguage,
  supportedLanguages,
  config,
}: ISelectLanguageProps) {
  const { enable_openai_transcription: enableOpenaiTranscription } = useFlags(
    [],
    [FlagsmithTraits.ENABLE_OPENAI_TRANSCRIPTION],
  );
  const form = useForm<z.infer<typeof SelectLanguageSchema>>({
    resolver: zodResolver(SelectLanguageSchema),
  });
  if (language) return null;
  const options = supportedLanguages?.length
    ? LANGUAGES.filter((lang) =>
        supportedLanguages.some((supported) => supported.code === lang.code),
      )
    : LANGUAGES;

  return (
    <Form {...form}>
      <div className="fixed top-0 bottom-0 w-full bg-black bg-opacity-50 z-50">
        <form
          onSubmit={form.handleSubmit((data) => {
            setLanguage(data.language);
          })}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] flex flex-col bg-white border border-secondary rounded-xl p-3.5 gap-4 shadow-md drop-shadow-md"
        >
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Select your preferred language</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a language" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="max-h-64">
                    {options
                      .filter(({ code }) =>
                        enableOpenaiTranscription ? code !== "multi" : true,
                      )
                      .map(({ code, label }) => (
                        <SelectItem
                          className="focus:bg-neutral-400"
                          value={code}
                        >
                          {label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            style={{
              backgroundColor: getHSLAString(config.brandColor),
              color: config.textColor === "light" ? "white" : "black",
            }}
          >
            Start Chat
          </Button>
        </form>
      </div>
    </Form>
  );
}
